import {
  DoneOutlined,
  EditOutlined,
  ExpandLess,
  ExpandMore,
  LockOutlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Typography,
} from "@mui/material";
import { ReactNode } from "react";

export type StepStatus = "complete" | "current" | "locked";

export const StepAccordion = ({
  stepNumber,
  title,
  status,
  subtitle,
  missingRequirementCount = 0,
  waitingForApproval = false,
  actions,
  expanded,
  disabled = false,
  onExpandedChange,
  detailsLabel,
  children,
}: {
  stepNumber: number;
  title: string;
  status: StepStatus;
  subtitle?: ReactNode;
  missingRequirementCount?: number;
  waitingForApproval?: boolean;
  actions?: ReactNode;
  expanded: boolean;
  disabled?: boolean;
  onExpandedChange: (expanded: boolean) => void;
  detailsLabel: string;
  children: ReactNode;
}) => (
  <Accordion
    className="overflow-hidden rounded-none border-0 before:hidden [&.Mui-expanded]:bg-[#074ee814]"
    expanded={expanded}
    disabled={disabled}
    onChange={(_event, nextExpanded) => onExpandedChange(nextExpanded)}
    disableGutters
  >
    <div
      role="group"
      aria-label={`${title} header`}
      className="flex flex-wrap items-center gap-x-3 px-[10.5px] py-[7px] hover:bg-color-bg"
    >
      <AccordionSummary className="min-h-0 min-w-0 flex-1 basis-48 bg-transparent p-0">
        <div className="grid w-full grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-1 text-left">
          <Typography component="span" variant="body2" className="sr-only">
            {stepNumber}
          </Typography>
          <Typography variant="body1" className="col-start-2">
            {title}
          </Typography>
          <span className="col-start-1 row-start-1 inline-flex h-8 items-center justify-center">
            {status === "complete" ? (
              <DoneOutlined
                fontSize="small"
                color="success"
                titleAccess="Complete"
              />
            ) : status === "current" ? (
              <EditOutlined fontSize="small" titleAccess="In progress" />
            ) : (
              <LockOutlined fontSize="small" titleAccess="Locked" />
            )}
          </span>
          {subtitle && (
            <Typography
              className="col-start-2"
              variant="caption"
              color="text.secondary"
            >
              {subtitle}
            </Typography>
          )}
        </div>
      </AccordionSummary>
      <div className="ml-auto flex items-center gap-3">
        {missingRequirementCount > 0 ? (
          <Typography
            component="span"
            variant="caption"
            color="error"
            className="whitespace-nowrap"
          >
            {missingRequirementCount} field
            {missingRequirementCount === 1 ? "" : "s"} missing
          </Typography>
        ) : waitingForApproval ? (
          <Typography
            component="span"
            variant="caption"
            color="warning.main"
            className="whitespace-nowrap"
          >
            Waiting for approval
          </Typography>
        ) : null}
        {actions}
        <IconButton
          size="small"
          disabled={disabled}
          aria-label={`${expanded ? "Collapse" : "Expand"} step ${stepNumber}`}
          aria-expanded={expanded}
          onClick={() => onExpandedChange(!expanded)}
        >
          {expanded ? (
            <ExpandLess fontSize="small" />
          ) : (
            <ExpandMore fontSize="small" />
          )}
        </IconButton>
      </div>
    </div>
    <AccordionDetails
      role="region"
      aria-label={detailsLabel}
      className="flex flex-col gap-4 bg-white px-[10.5px] py-[7px]"
    >
      {children}
    </AccordionDetails>
  </Accordion>
);
